import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Device } from '../services/deviceService';
import streamService, { Stream } from '../services/streamService';
import videoPostService, { VideoPost } from '../services/videoPostService';

interface DeviceCardListProps {
  devices: Device[];
}

const DeviceCardList: React.FC<DeviceCardListProps> = ({ devices }) => {
  const navigate = useNavigate();
  const [deviceStreams, setDeviceStreams] = useState<Map<number, Stream[]>>(new Map());
  const [deviceVideoPosts, setDeviceVideoPosts] = useState<Map<number, VideoPost[]>>(new Map());

  useEffect(() => {
    const loadAllStreamsAndVideos = async () => {
      const streamsMap = new Map<number, Stream[]>();
      const videoPostsMap = new Map<number, VideoPost[]>();
      
      for (const device of devices) {
        try {
          const streams = await streamService.getByDeviceId(device.id);
          streamsMap.set(device.id, streams);
        } catch (err) {
          console.error(`Failed to load streams for device ${device.id}:`, err);
        }

        try {
          const videoPosts = await videoPostService.getByDeviceId(device.id);
          videoPostsMap.set(device.id, videoPosts);
        } catch (err) {
          console.error(`Failed to load video posts for device ${device.id}:`, err);
        }
      }
      
      setDeviceStreams(streamsMap);
      setDeviceVideoPosts(videoPostsMap);
    };

    if (devices.length > 0) {
      loadAllStreamsAndVideos();
    }
  }, [devices]);

  const getActiveLED = (deviceId: number): { color: string; blink: boolean; label: string } | null => {
    const streams = deviceStreams.get(deviceId) || [];
    const videoPosts = deviceVideoPosts.get(deviceId) || [];
    
    // Check for live streams (higher priority)
    const liveStreams = streams
      .filter(s => s.islive && !s.muted && !s.watching)
      .sort((a, b) => a.priority - b.priority);
    
    if (liveStreams.length > 0) {
      const stream = liveStreams[0];
      return {
        color: `rgb(${stream.red}, ${stream.green}, ${stream.blue})`,
        blink: stream.blink,
        label: `🔴 ${stream.channelName || stream.channelID}`
      };
    }
    
    // Check for new video posts
    const newVideoPosts = videoPosts
      .filter(v => v.hasNewVideo && !v.muted && !v.watching)
      .sort((a, b) => a.priority - b.priority);
    
    if (newVideoPosts.length > 0) {
      const videoPost = newVideoPosts[0];
      return {
        color: `rgb(${videoPost.red}, ${videoPost.green}, ${videoPost.blue})`,
        blink: videoPost.blink,
        label: `📹 ${videoPost.channelName || videoPost.channelID}`
      };
    }
    
    return null;
  };

  if (devices.length === 0) {
    return (
      <div className="no-devices">
        <p>No devices added yet. Add your first device above!</p>
      </div>
    );
  }

  return (
    <div className="device-cards-grid">
      {devices.map((device) => {
        const activeLED = getActiveLED(device.id);
        const ledColor = activeLED?.color;
        
        return (
          <div
            key={device.id}
            className="device-card-item"
            onClick={() => navigate(`/device/${device.id}`)}
          >
            <div className="device-card-icon">
              {activeLED ? (
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: device.device_type === 'Single Color' ? '#9146ff' : ledColor || 'transparent',
                    boxShadow: '0 0 20px rgba(0, 0, 0, 0.3), inset 0 0 15px rgba(255, 255, 255, 0.3)',
                    border: '3px solid rgba(255, 255, 255, 0.5)',
                    animation: activeLED.blink ? 'blink 1s ease-in-out infinite' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="LED is ON"
                >
                </div>
              ) : (
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: '#2a2a2a',
                    border: '3px solid #444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                  }}
                  title="LED is OFF"
                >
                </div>
              )}
            </div>
            <div className="device-card-info">
              <h3>{device.device_name || 'Unnamed Device'}</h3>
              <p className="device-card-hash">{device.device_hash}</p>
              {activeLED && (
                <p className="device-card-status" style={{ marginTop: 8, fontSize: 13, color: '#28a745', fontWeight: 600 }}>
                  {activeLED.label}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DeviceCardList;
